import type { DefaultRootState } from 'react-redux';
import type { Dispatch } from 'redux';

import type { AllAlert } from '~/@types/redux';
import {
  getNextClassifyAlert,
  getVisibleCategoryId,
  resolveNextNavigation,
} from '~/common/util/ruleUtils';
import { CATEGORY_IDS } from '~/pages/Home/constants';

import lang from '../language.json';
import TYPES from './types';

// set data
const setOnInit = (value) => ({
  type: TYPES.SET_ON_INIT,
  value,
});

const setSelectedCategory = (category) => ({
  type: TYPES.SET_SELECTED_CATEGORY,
  category,
});

const setClickedSelectedCategory = (category) => ({
  type: TYPES.SET_CLICKED_SELECTED_CATEGORY,
  category,
});

const setSelectedAlert = (alert) => ({
  type: TYPES.SET_SELECTED_ALERT,
  alert,
});

// Sets both together so no render ever observes a selectedCategory that
// doesn't match selectedAlert's own category (see nextAlert/classifyNextAlert).
const setSelectedAlertAndCategory = (alert, category) => ({
  type: TYPES.SET_SELECTED_ALERT_AND_CATEGORY,
  alert,
  category,
});

const setSelectedAlertParent = (alert) => ({
  type: TYPES.SET_SELECTED_ALERT_PARENT,
  alert,
});

const setShouldGetNextAlert = (value) => ({
  type: TYPES.SET_SHOULD_GET_NEXT_ALERT,
  value,
});

const setIsLastAlert = (value) => ({
  type: TYPES.SET_IS_LAST_ALERT,
  value,
});

const setIsUnderwriterCheckboxDisabled = (value) => ({
  type: TYPES.SET_IS_UNDERWRITER_CHECKBOX_DISABLED,
  value,
});

// navigation items
const getNavigationDetailsStarted = () => ({
  type: TYPES.GET_NAVIGATION_ITEMS_STARTED,
});

const getNavigationDetailsSuccess = (payload) => {
  return {
    type: TYPES.GET_NAVIGATION_ITEMS_SUCCESS,
    allAlerts: payload?.allAlerts,
    categories: payload?.categories,
    allRequiredRulesCompleted: payload?.allRequiredRulesCompleted,
    loanLockInformation: payload?.loanLockInformation,
  };
};

const getNavigationDetailsError = () => ({
  type: TYPES.GET_NAVIGATION_ITEMS_ERROR,
  error: lang.GET_NAVIGATION_DETAILS_ERROR_MESSAGE,
});

// trigger all rules
const triggerAllRulesStarted = () => ({
  type: TYPES.POST_TRIGGER_ALL_RULES_STARTED,
});

const triggerAllRulesSuccess = () => ({
  type: TYPES.POST_TRIGGER_ALL_RULES_SUCCESS,
});

const triggerAllRulesError = () => ({
  type: TYPES.POST_TRIGGER_ALL_RULES_ERROR,
  error: lang.POST_TRIGGER_ALL_RULES_ERROR_MESSAGE,
});

// underwriter
const getUnderwriterVerificationStarted = () => ({
  type: TYPES.POST_GET_UNDERWRITER_VERIFICATION_STATUS_STARTED,
});

const getUnderwriterVerificationSuccess = (payload) => ({
  type: TYPES.POST_GET_UNDERWRITER_VERIFICATION_STATUS_SUCCESS,
  payload,
});

const getUnderwriterVerificationError = () => ({
  type: TYPES.POST_GET_UNDERWRITER_VERIFICATION_STATUS_ERROR,
  error: lang.GET_UNDERWRITER_VERIFICATION_STATUS_ERROR,
});

const saveUnderwriterVerificationStarted = () => ({
  type: TYPES.POST_SAVE_UNDERWRITER_VERIFICATION_STARTED,
});

const saveUnderwriterVerificationSuccess = ({
  ruleResultID,
  isUnderwriterVerified,
}: { ruleResultID?: unknown; isUnderwriterVerified?: unknown } = {}) => ({
  type: TYPES.POST_SAVE_UNDERWRITER_VERIFICATION_SUCCESS,
  ruleResultID,
  isUnderwriterVerified,
});

const saveUnderwriterVerificationError = () => ({
  type: TYPES.POST_SAVE_UNDERWRITER_VERIFICATION_ERROR,
  error: lang.SAVE_UNDERWRITER_RULE_VERIFICATION_ERROR_MESSAGE,
});

const setUnderWriterViewPermissions = (payload: {
  hasAccessToUnderwriterView: boolean;
  canWriteUnderwriterCheckbox: boolean;
  canOverrideAmcCheckbox: boolean;
}) => ({
  type: TYPES.SET_UNDERWRITER_VIEW_PERMISSIONS,
  payload,
});

const nextAlert =
  (isUnderwriter: boolean) => (dispatch, getState: () => DefaultRootState) => {
    const {
      home: {
        selectedAlert,
        selectedCategory,
        navigationItems: { allAlerts },
        user,
      },
    } = getState();

    // hasAccessToUnderwriterView (TRAC/senior-view) also reviews cleared
    // items, so it must use the same linear underwriter navigation.
    const isUnderwriterNav =
      isUnderwriter || !!user?.hasAccessToUnderwriterView;

    const result = resolveNextNavigation(
      allAlerts,
      selectedAlert,
      isUnderwriterNav,
    );

    switch (result.action) {
      case 'NAVIGATE': {
        const currentCategoryId =
          selectedCategory ??
          (selectedAlert ? getVisibleCategoryId(selectedAlert) : undefined);
        const nextCategoryId = getVisibleCategoryId(result.alert);
        dispatch(setShouldGetNextAlert(false));
        if (currentCategoryId === nextCategoryId) {
          dispatch(setSelectedAlert(result.alert));
        } else {
          dispatch(setSelectedAlertAndCategory(result.alert, nextCategoryId));
        }
        break;
      }
      case 'LAST_ALERT':
        dispatch(setIsLastAlert(true));
        break;
    }
  };

const classifyNextAlert =
  (
    action: 'next' | 'continue',
    isUnderwriter = false,
    nextClassifyRuleReferenceId?: number,
    hasActionableClassifyDocuments?: boolean,
  ) =>
  (dispatch: Dispatch, getState: () => DefaultRootState) => {
    const {
      selectedAlert,
      navigationItems: { allAlerts },
      user,
    } = getState().home;

    // hasAccessToUnderwriterView (TRAC/senior-view) also reviews cleared
    // items, so it must use the same linear underwriter navigation.
    const isUnderwriterNav =
      isUnderwriter || !!user?.hasAccessToUnderwriterView;

    dispatch(setShouldGetNextAlert(false));
    dispatch(setIsUnderwriterCheckboxDisabled(false));

    const shouldLeaveClassify =
      action === 'continue' &&
      getVisibleCategoryId(selectedAlert) === CATEGORY_IDS.CLASSIFY &&
      (isUnderwriterNav || hasActionableClassifyDocuments === false);

    let next: AllAlert | undefined;

    if (action === 'next') {
      // prefer the stored pre-save alert - derive it from the current list for callers without a stored ID
      next =
        nextClassifyRuleReferenceId === undefined
          ? getNextClassifyAlert(allAlerts, selectedAlert)
          : allAlerts.find(
              (alert) =>
                alert.ruleReferenceId === nextClassifyRuleReferenceId &&
                getVisibleCategoryId(alert) === CATEGORY_IDS.CLASSIFY,
            );
    }

    if (!next) {
      // continue and unresolved next actions use the standard navigation rules
      const navigationAlerts = shouldLeaveClassify
        ? allAlerts.filter(
            (alert) => getVisibleCategoryId(alert) !== CATEGORY_IDS.CLASSIFY,
          )
        : allAlerts;
      const nextNavigation = resolveNextNavigation(
        navigationAlerts,
        selectedAlert,
        isUnderwriterNav,
      );

      if (nextNavigation.action === 'NAVIGATE') {
        next = nextNavigation.alert;
      }
    }

    if (!next) {
      dispatch(setIsLastAlert(true));
      return;
    }

    const nextCategoryId = getVisibleCategoryId(next);
    if (nextCategoryId === CATEGORY_IDS.CLASSIFY) {
      dispatch(setSelectedAlert(next));
    } else {
      dispatch(setSelectedAlertAndCategory(next, nextCategoryId));
    }
  };

export default {
  getNavigationDetailsStarted,
  getNavigationDetailsSuccess,
  getNavigationDetailsError,
  triggerAllRulesStarted,
  triggerAllRulesSuccess,
  triggerAllRulesError,
  setSelectedCategory,
  setClickedSelectedCategory,
  setSelectedAlert,
  setSelectedAlertAndCategory,
  setSelectedAlertParent,
  setShouldGetNextAlert,
  setIsLastAlert,
  getUnderwriterVerificationStarted,
  getUnderwriterVerificationSuccess,
  getUnderwriterVerificationError,
  saveUnderwriterVerificationStarted,
  saveUnderwriterVerificationSuccess,
  saveUnderwriterVerificationError,
  nextAlert,
  classifyNextAlert,
  setOnInit,
  setIsUnderwriterCheckboxDisabled,
  setUnderWriterViewPermissions,
};
